import { processRawValue } from '@atlaskit/editor-common/process-raw-value';
import { Node as PMNode, type Schema } from '@atlaskit/editor-prosemirror/model';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import { Transform } from '@atlaskit/editor-prosemirror/transform';

import type { Suggestion } from '../../aiSuggestionsPluginType';

import { wrapNode } from './toolCalls/wrapNode';

function createNodeFromToolCallValue(schema: Schema, value: string): PMNode | undefined {
	try {
		const parsed = typeof value === 'string' ? JSON.parse(value) : value;
		const node = processRawValue(schema, parsed);
		return node;
	} catch {
		return undefined;
	}
}

export const getInversion = (tr: Transaction, suggestion: Suggestion) => {
	const localIdToPos = new Map<string, { from: number; to: number }>();
	tr.doc.descendants((node, pos) => {
		const localId = node.attrs?.localId;
		if (localId) {
			localIdToPos.set(localId, { from: pos, to: pos + node.nodeSize });
		}
		return true;
	});
	/**
	 * For some reason, without transforming the doc back and forth,
	 * the steps are missing in subsequent transforms.
	 */
	const transform = new Transform(PMNode.fromJSON(tr.doc.type.schema, tr.doc.toJSON()));
	suggestion.toolCalls.forEach((toolCall) => {
		const localId = toolCall.arguments?.localId;
		if (localId === undefined) {
			return;
		}

		const pos = localIdToPos.get(localId);
		if (!pos) {
			return;
		}

		if ('value' in toolCall.arguments) {
			const value = toolCall.arguments.value;
			const node = createNodeFromToolCallValue(tr.doc.type.schema, value);
			if (!node) {
				return;
			}

			switch (toolCall.name) {
				case 'replaceNode':
					transform.replaceWith(pos.from, pos.to, node);
					break;
				case 'insertNodeBefore':
					transform.insert(pos.from, node);
					break;
				case 'insertNodeAfter':
					transform.insert(pos.to, node);
					break;
			}
		} else {
			switch (toolCall.name) {
				case 'removeNode':
					transform.delete(pos.from, pos.to);
					break;
				case 'wrap':
				case 'wrapNode':
					wrapNode({
						toolCallArguments: toolCall.arguments,
						tr: transform,
						pos: pos.from,
					});

					break;
				case 'setAttrs':
					const node = transform.doc.nodeAt(pos.from);
					if (!node) {
						break;
					}
					transform.setNodeMarkup(pos.from, undefined, node.attrs);
					break;
				default:
					break;
			}
		}
	});
	return transform;
};
